import type { TradingAccount } from '../types';

/**
 * Masque les informations sensibles d'un compte trading
 * @param account Le compte trading à sécuriser
 * @returns Une copie du compte avec les informations sensibles masquées
 */
export function sanitizeAccount(account: TradingAccount): TradingAccount {
  // Créer une copie profonde de l'objet
  const sanitizedAccount = JSON.parse(JSON.stringify(account)) as TradingAccount;
  
  // Masquer le mot de passe MetaTrader s'il existe
  if (sanitizedAccount.metaTrader && sanitizedAccount.metaTrader.password) {
    sanitizedAccount.metaTrader.password = '********';
  }
  
  return sanitizedAccount;
}

/**
 * Vérifie si un objet est sécurisé pour être affiché dans la console
 * @param obj L'objet à vérifier
 * @returns L'objet sécurisé pour l'affichage
 */
export function sanitizeForLogging(obj: any): any {
  if (!obj) return obj;
  
  // Si c'est un compte trading, utiliser la fonction dédiée
  if (obj.metaTrader && typeof obj.metaTrader === 'object') {
    return sanitizeAccount(obj);
  }
  
  // Pour les autres types d'objets, créer une copie et masquer les propriétés sensibles connues
  const sanitized = { ...obj };
  
  const sensitiveKeys = ['password', 'secret', 'token', 'key', 'apiKey', 'api_key'];
  
  // Parcourir les propriétés de premier niveau
  for (const key of Object.keys(sanitized)) {
    // Si la clé contient un mot sensible
    if (sensitiveKeys.some(sensitiveKey => key.toLowerCase().includes(sensitiveKey.toLowerCase()))) {
      sanitized[key] = '********';
    }
    // Si la valeur est un objet, récursivement sécuriser cet objet
    else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeForLogging(sanitized[key]);
    }
  }
  
  return sanitized;
}

/**
 * Version sécurisée de log qui masque les informations sensibles
 * Note: Tous les console.log ont été supprimés
 */
export const secureLog = {
  log: (...args: any[]) => {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? sanitizeForLogging(arg) : arg
    );
    // console.log supprimé
  },
  
  error: (...args: any[]) => {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? sanitizeForLogging(arg) : arg
    );
    // console.error supprimé
  },
  
  warn: (...args: any[]) => {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? sanitizeForLogging(arg) : arg
    );
    // console.warn supprimé
  },
  
  info: (...args: any[]) => {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? sanitizeForLogging(arg) : arg
    );
    // console.info supprimé
  }
};
